import React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import t from 'react-translate';
import NvAutocompleteBase from 'shared/components/nv-autocomplete-base';
import ClickableContainer, { ClickableContainerProps } from 'components/clickable-container';
import {
  info,
  gray2,
  gray4,
  primary,
} from 'styles/global_defaults/colors';
import {
  halfSpacing,
  doubleSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { openSans } from 'styles/global_defaults/fonts';

const RESULTS_CONTAINER_BORDER_WIDTH = 1;
const RESULT_ITEM_HEIGHT = doubleSpacing;

// Generic type for items that can be searched
export type SearchableItem = {
  catalogId: string;
  name: string;
  [key: string]: any; // Allow additional properties
};
export type SearchResultItemProps<T> = ClickableContainerProps & {
  item: T;
  denied?: boolean;
  isToggle?: boolean;
  onSelectItem?: (item: T) => void
  renderContent?: (item: T) => React.ReactNode;
};
// Type for custom search result item component
export type SearchResultItemComponent<T> = React.ComponentType<SearchResultItemProps<T>>;

type Props<T extends SearchableItem> = {
  // Required props
  fetchPredictions: (hint: string) => Promise<T[] | null> | T[] | null;
  keyExtractor: (result: T, index: number) => string | number,

  // Customization props
  placeholder?: string;
  emptyResultsText?: string;
  searchingText?: string;
  addTooltipText?: string;
  deniedTooltipText?: string;
  className?: string;
  autocompleteClassName?: string;

  // Item rendering
  onSelectItem?: (item: T) => void
  searchResultItemContent?: (item: T) => React.ReactNode;
  SearchResultItem?: SearchResultItemComponent<T>; // Custom result item component
  alwaysShowInput?: boolean;
  searchInputExtraContent?: React.ReactNode;
  searchInputClassName?: string;

  extraStyles?: SerializedStyles;

  // Validation
  deniedItemsDictionary?: { [key: string]: boolean };
  isItemDenied?: (item: T) => boolean;

  // Configuration
  maxResults?: number;
};

const GenericSearchInput = <T extends SearchableItem>(props: Props<T>) => {
  const {
    fetchPredictions,
    keyExtractor,
    placeholder = t.SEARCH.SEARCH(),
    emptyResultsText = t.SEARCH.NO_RESULTS_FOUND(),
    searchingText = t.SEARCH.LOADING_RESULTS(),
    extraStyles,
    autocompleteClassName,
    onSelectItem: onSelectResultItem,
    searchResultItemContent,
    SearchResultItem: CustomSearchResultItem, // Custom component
    alwaysShowInput = false,
    searchInputExtraContent,
    searchInputClassName,
    deniedItemsDictionary,
    isItemDenied,
    maxResults = 5,
  } = props;

  const [selectedItem, setSelectedItem] = React.useState<T | null>(null);

  const checkIfItemDenied = (item: T): boolean => {
    if (isItemDenied) {
      return isItemDenied(item);
    }
    return !!deniedItemsDictionary?.[item.catalogId];
  };

  const onSelectItem = (item: T) => {
    setSelectedItem(item);
    onSelectResultItem?.(item);
  };

  const isCurrentItemDenied = selectedItem ? checkIfItemDenied(selectedItem) : false;

  // Use custom search result item or fall back to default
  const ResultItemComponent = CustomSearchResultItem || DefaultSearchResultItem;

  const styles = css`
      ${extraStyles}
      .autocomplete-results-container {
        max-height: ${(RESULT_ITEM_HEIGHT * maxResults) + (RESULTS_CONTAINER_BORDER_WIDTH * 2)}px;
      }
    `;


  return (
    <NvAutocompleteBase<T>
      css={styles}
      value={selectedItem}
      onChange={onSelectItem}
      className={autocompleteClassName}
      fetchPredictions={fetchPredictions}
      keyExtractor={keyExtractor}
      resultsContainerClassName='autocomplete-results-container'
      renderLoadingIndicator={() => (
        <LoadingIndicator loadingText={searchingText} />
      )}
      renderNoResults={() => (
        <PresentationalOption text={emptyResultsText} />
      )}
      alwaysShowInput={alwaysShowInput}
      renderInput={(inputProps) => (
        <SearchInput
          placeholder={placeholder}
          searchInputExtraContent={searchInputExtraContent}
          className={searchInputClassName}
          {...inputProps}
        />
      )}
      renderResultItem={(resultItemProps, item) => (
        <ResultItemComponent
          item={item}
          denied={checkIfItemDenied(item)}
          renderContent={searchResultItemContent}
          onSelectItem={onSelectItem}
          {...resultItemProps}
        />
      )}
      renderValueSelected={(resultItemProps, item) => (
        <ResultItemComponent
          isToggle
          item={item}
          denied={isCurrentItemDenied}
          renderContent={searchResultItemContent}
          onSelectItem={onSelectItem}
          {...resultItemProps}
        />
      )}
    />
  );
};


// Default Search Result Item Component
const DefaultSearchResultItem = <T extends SearchableItem>(props: SearchResultItemProps<T>) => {
  const {
    denied,
    item,
    className,
    isToggle = false,
    renderContent,
    onSelectItem,
    ...restProps
  } = props;

  const sharedContentStyles = css`
    background-color: #fff;
    height: ${doubleSpacing}px;
    padding: 0 ${halfSpacing}px;
    line-height: ${doubleSpacing}px;
  `;

  const sharedInputStyles = css`
    flex: 1;
    border: 1px solid;
    border-color: ${gray4};
    height: ${doubleSpacing}px;

    &:focus {
      border-color: ${primary};
    }
  `;

  const styles = css`
    width: 100%;
    border: none;
    display: block;
    text-align: left;
    position: relative;
    ${sharedContentStyles};

    .item-icon {
      display: inline-block;
      margin-right: ${quarterSpacing}px;
    }

    ${denied && css`
      color: ${gray4};
      font-style: italic;
    `};

    ${isToggle ? css`
      border-color: ${primary};
      padding-right: ${halfSpacing * 3}px;
      ${sharedInputStyles};
    ` : css`
      &:hover, &:focus {
        background-color: ${info};
      }
    `};
  `;

  return (
    <ClickableContainer
      css={styles}
      className={`ellipsis${(isToggle && className) ? ` ${className}` : ''}`}
      onClick={() => onSelectItem?.(item)}
      {...restProps}
    >
      {renderContent(item)}
    </ClickableContainer>
  );
};

type SearchInputProps = Omit<React.ComponentProps<'input'>, 'type'> & {
  searchInputExtraContent?: React.ReactNode;
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>((props, ref) => {
  const { onBlur, onFocus, className, searchInputExtraContent, ...restProps } = props;
  const [isFocused, setIsFocused] = React.useState(false);

  const sharedInputStyles = css`
    flex: 1;
    border: 1px solid;
    border-color: ${gray4};
    height: ${doubleSpacing}px;

    &:focus {
      border-color: ${primary};
    }
  `;

  const styles = css`
    position: relative;
    ${sharedInputStyles};
    ${isFocused && css`
      border-color: ${primary};
    `};

    input {
      width: 100%;
      height: 100%;
      border: none;
      outline: none;
      font-size: 14px;
      font-weight: 400;
      font-family: ${openSans};
      line-height: ${standardSpacing}px;

      ::placeholder {
        color: ${gray2};
      }
    }
  `;

  const handleInputBlur = (event?: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    setIsFocused(false);
  };

  const handleInputFocus = (event?: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    setIsFocused(true);
  };

  const toggleFocus = () => {
    if (!isFocused) {
      handleInputFocus();
    } else {
      handleInputBlur();
    }
  };

  // Add onClick to extra content if it's an element and has a prop
  const renderExtraContent = () => {
    if (React.isValidElement(searchInputExtraContent)) {
      const originalProps = searchInputExtraContent.props || {};
      return React.cloneElement(
        searchInputExtraContent,
        {
          ...originalProps,
          onClick: originalProps.onClick ?? toggleFocus,
        },
      );
    }
    return searchInputExtraContent;
  };

  return (
    <div css={styles} className={className}>
      <input
        ref={ref}
        type='text'
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        {...restProps}
      />
      {searchInputExtraContent && renderExtraContent()}
    </div>
  );
});


type PresentationalOptionProps = {
  text: string;
};

const PresentationalOption = (props: PresentationalOptionProps) => {
  const commonTextStyles = css`
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    font-family: ${openSans};
  `;

  const sharedContentStyles = css`
    background-color: #fff;
    height: ${doubleSpacing}px;
    padding: 0 ${halfSpacing}px;
    line-height: ${doubleSpacing}px;
  `;

  const styles = css`
    display: flex;
    align-items: center;
    ${sharedContentStyles};
    ${commonTextStyles};
  `;

  return <div css={styles}>{props.text}</div>;
};

type LoadingIndicatorProps = {
  loadingText: string;
};

const LoadingIndicator = (props: LoadingIndicatorProps) => {
  const loadingText = NvAutocompleteBase.useLoadingText(props.loadingText);
  return <PresentationalOption text={loadingText} />;
};

export default GenericSearchInput;
